import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import { toLower } from 'lodash';
import {
  MultiHeader,
  SingleHeader,
  SingleDataColumn,
  MultiDataColumn
} from '../Components';
import { ResultTableWrapper } from './styled';
import { convertISOToDateFormat } from '../../../../util/timezone';
import { TextComponent } from '../../components/TableComponents';
import {
  TM_FRAUD_PREVENTION_SOURCE_MAPPING,
  TM_FRAUD_SOURCE_IDS
} from '../../../../util/GlobalConstant';

const PreventionResultTable = ({
  isLoading,
  results,
  paginationProps,
  onPaginationChange,
  totalResults,
  onAlertIdClick,
  hasAccessToAlertDetails,
  typlogyActions,
  fetchTypologiesActions,
  alertLevels
}) => {
  useEffect(() => {
    if (!typlogyActions?.length) {
      fetchTypologiesActions();
    }
  }, []);

  const preventionAlertColors = (key) => {
    return typlogyActions?.find(
      ({ displayName }) => toLower(displayName) === toLower(key)
    )?.alertLevelColor;
  };

  const columns = [
    {
      title: (
        <MultiHeader header="Alert ID" subHeaders={['Alert Generation Date']} />
      ),
      width: '15%',
      ellipsis: true,
      render: (_, record) => {
        const {
          alertId = '-',
          alertDate = '-',
          reviewStatus = 'Not Started'
        } = record;

        if (hasAccessToAlertDetails) {
          return (
            <MultiDataColumn.Link
              pop
              data={[
                {
                  text: alertId,
                  to: `/app/trm/run-instance/${alertId}`,
                  ...(toLower(reviewStatus) === 'not started'
                    ? {
                        onClick: () => {
                          onAlertIdClick(alertId);
                        }
                      }
                    : {})
                },
                {
                  text: convertISOToDateFormat(alertDate) || '-'
                }
              ]}
            />
          );
        } else {
          return (
            <MultiDataColumn
              data={[
                { text: alertId },
                {
                  text: convertISOToDateFormat(alertDate) || '-'
                }
              ]}
              pop
            />
          );
        }
      }
    },
    // {
    //   title: <MultiHeader header="Party ID" subHeaders={['Party Name']} />,
    //   width: '15%',
    //   ellipsis: true,
    //   render: (_, record) => {
    //     const { primaryPartyKey = '-', primaryPartyName = '-' } = record;
    //     return (
    //       <MultiDataColumn
    //         data={[{ text: primaryPartyKey }, { text: primaryPartyName }]}
    //         pop
    //       />
    //     );
    //   }
    // },
    // {
    //   title: <MultiHeader header="Account NO" subHeaders={['Account Type']} />,
    //   width: '10%',
    //   ellipsis: true,
    //   render: (_, record) => {
    //     const { accountId = '-', accountType = '-' } = record;
    //     return (
    //       <MultiDataColumn
    //         pop
    //         data={[{ text: accountId }, { text: accountType }]}
    //       />
    //     );
    //   }
    // },
    {
      title: (
        <MultiHeader
          header="Run Instance Name"
          subHeaders={['Run Instance ID', 'Run Instance Execution ID']}
        />
      ),
      width: '13%',
      ellipsis: true,
      render: (_, record) => {
        const {
          runInstanceName = '-',
          runInstanceId = '-',
          runInstanceExecutionId = '-'
        } = record;
        return (
          <MultiDataColumn
            pop
            data={[
              { text: runInstanceName },
              { text: runInstanceId },
              { text: runInstanceExecutionId }
            ]}
          />
        );
      }
    },
    // {
    //   title: (
    //     <MultiHeader header="Typology Name" subHeaders={['Typology ID']} />
    //   ),
    //   width: '10%',
    //   ellipsis: true,
    //   render: (_, record) => {
    //     const { triggeredTypologies = [] } = record;
    //     const typologyNames = map(triggeredTypologies, 'name').join(', ');
    //     const typologyIds = map(triggeredTypologies, 'id').join(', ');
    //     return (
    //       <MultiDataColumn
    //         pop
    //         data={[
    //           { text: isEmpty(typologyNames) ? '-' : typologyNames },
    //           { text: isEmpty(typologyIds) ? '-' : typologyIds }
    //         ]}
    //       />
    //     );
    //   }
    // },
    {
      title: <SingleHeader header="FinCrime Type" />,
      width: '9%',
      render: (_, record) => {
        const { alertSourceId = '' } = record;
        return (
          <TextComponent
            style={{ marginLeft: 0 }}
            id={
              alertSourceId === TM_FRAUD_SOURCE_IDS.TM
                ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.TM_AML
                : alertSourceId === TM_FRAUD_SOURCE_IDS.FRAUD_PREVENTION_ID
                ? TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_PREVENTION
                : TM_FRAUD_PREVENTION_SOURCE_MAPPING.FRAUD_DETECTION
            }
          />
        );
      }
    },
    {
      title: <SingleHeader header="Actions taken for the variants breached" />,
      width: '10%',
      render: (_, record) => {
        const { triggeredRulesActionSummary = '-' } = record;
        if (triggeredRulesActionSummary == '-') {
          return '-';
        }
        if (triggeredRulesActionSummary && typlogyActions) {
          const triggerConverted = JSON.parse(triggeredRulesActionSummary);
          const keys = Object.keys(triggerConverted);
          const values = Object.values(triggerConverted);
          return (
            <>
              {keys.map((key, index) => (
                <div
                  key={key}
                  style={{
                    color: preventionAlertColors(key)
                  }}>
                  {key}: {values[index]}
                </div>
              ))}
            </>
          );
        }
      }
    },
    {
      title: <SingleHeader header="Review Status" />,
      width: '9%',
      render: (_, record) => {
        const { reviewStatus = 'Not Started' } = record;
        return <SingleDataColumn.Status type={reviewStatus} />;
      }
    },
    {
      title: <SingleHeader header="Review Feedback" />,
      width: '10%',
      render: (_, record) => {
        const { alertQuality = '-' } = record;
        return (
          <SingleDataColumn.Feedback
            type={alertQuality}
            alertLevels={alertLevels}
          />
        );
      }
    }
  ];

  return (
    <ResultTableWrapper>
      <Table
        loading={isLoading}
        columns={columns}
        dataSource={results}
        rowKey="alertId"
        pagination={{
          total: totalResults,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: onPaginationChange,
          showTotal: (total) => `${total} Records`,
          ...paginationProps
        }}
      />
    </ResultTableWrapper>
  );
};

PreventionResultTable.propTypes = {
  results: PropTypes.array,
  isLoading: PropTypes.bool,
  totalResults: PropTypes.number,
  paginationProps: PropTypes.object,
  onPaginationChange: PropTypes.func,
  onAlertIdClick: PropTypes.func,
  typlogyActions: PropTypes.array,
  alertLevels: PropTypes.array
};

export default PreventionResultTable;
